import flowRight from 'lodash/flowRight'
import React, { useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { organizationSettingSelector } from 'shared/hocs/withAuth'
import Box from 'shared/components/Box'
import withDialog from 'shared/hocs/withDialog'
import { hideAllDialogs } from 'shared/redux/app/reducer'
import styled from 'styled-components'
import queryString from 'qs'
import SelectionControlGroup from 'shared/components/SelectionGroupControl'
import Button from 'shared/components/Button'
import useQuery from 'shared/hooks/useQuery'
import { NoteCategoryType } from 'pages/CustomizeScoreboard/model/noteCategory'

type Options = {
  label: string
  value: string
}

type SnackbarProps = {
  onCancel: Function
  onApply: Function
  sortByValue: string
  notesValue: string
  noteCategoryValue: string
  options: Options[]
}

function NoteFilter(props: SnackbarProps) {
  const { onApply, sortByValue, notesValue, noteCategoryValue, options, onCancel } = props
  const [sortByVal, setSortByVal] = useState<string>(sortByValue)
  const [notesVal, setNotesVal] = useState<string>(notesValue)
  const [noteCategory, setNoteCategory] = useState<string>(noteCategoryValue)
  const dispatch = useDispatch()
  const { organization_information } = useSelector(organizationSettingSelector)

  const noteCategoryQueryString = queryString.stringify({
    sort: [{ column: 'name', direction: 'asc' }],
  })

  const [noteCategoryQueryState] = useQuery<NoteCategoryType[]>(
    {
      url: `/note-category?${noteCategoryQueryString}`,
    },
    { isBase: true, initialData: [], skip: false },
  )

  const noteCategoryOptions = useMemo(() => {
    const options = [{ label: 'All Categories', value: 'all' }]
    noteCategoryQueryState.data.map(e => {
      options.push({ label: e.name, value: e.name })
    })
    return options
  }, [noteCategoryQueryState.data])

  const applyFilter = useCallback(() => {
    onApply({ sortByValue: sortByVal, notesValue: notesVal, noteCategoryValue: noteCategory })
  }, [onApply, sortByVal, notesVal, noteCategory])

  return (
    <NoteFiltersContainer>
      <FiltersContainer>
        <FilterSubContainer>
          <FilterTitle>View Notes</FilterTitle>
          <SelectionControlGroup
            id="view"
            name="view"
            direction="column"
            controls={options}
            value={notesVal}
            onChange={val => setNotesVal(val)}
            type="radio"
          />
        </FilterSubContainer>
        <FilterSubContainer>
          <FilterTitle>Sort By</FilterTitle>
          <SelectionControlGroup
            id="filter_id"
            name="filter_id"
            direction="column"
            controls={[
              {
                label: 'Newest to Oldest',
                value: 'desc',
              },
              {
                label: 'Oldest to Newest',
                value: 'asc',
              },
            ]}
            value={sortByVal}
            onChange={val => setSortByVal(val)}
            type="radio"
          />
        </FilterSubContainer>

        {organization_information.features.note.is_enabled && (
          <FilterSubContainer>
            <FilterTitle>Category</FilterTitle>
            <SelectionControlGroup
              id="categories"
              name="categories"
              direction="column"
              controls={noteCategoryOptions}
              value={noteCategory}
              onChange={val => setNoteCategory(val)}
              type="radio"
            />
          </FilterSubContainer>
        )}
      </FiltersContainer>

      <ActionButtonsContainer>
        <Button
          variant="outline-primary"
          size="large"
          width="140px"
          onClick={() => {
            if (onCancel) onCancel()
            else dispatch(hideAllDialogs())
          }}
        >
          Close
        </Button>
        <Button
          variant="primary"
          size="large"
          width="180px"
          onClick={() => {
            applyFilter()
          }}
        >
          Apply Filter
        </Button>
      </ActionButtonsContainer>
    </NoteFiltersContainer>
  )
}

const NoteFiltersContainer = styled(Box)`
  padding: 50px 30px 10px 30px;
`

const FilterTitle = styled(Box)`
  font-weight: bold;
  opacity: 0.9;
  margin-bottom: 20px;
`

const FiltersContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 60px;
  max-height: calc(100vh - 220px);
  overflow-x: hidden;
  overflow-y: auto;
`

const FilterSubContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 0 0 calc(50% - 10px);
`

const ActionButtonsContainer = styled(Box)`
  display: flex;
  justify-content: center;

  button {
    &:nth-child(2) {
      margin-left: 30px;
    }
  }
`

const AdvancedFiltersDialog = flowRight(withDialog())(NoteFilter)

AdvancedFiltersDialog.defaultProps = {
  hasCancel: false,
  hasContinue: false,
  footerClassName: 'dialog__footer--less-padding',
}

export default AdvancedFiltersDialog
